"use client";
import { API_URL } from "@/lib/config";

import {
  Compass, MapPin, Star, Eye, TrendingUp, Sparkles,
  FolderOpen, Loader2, ChevronRight, X, SlidersHorizontal,
  LucideIcon, ChevronDown, Map, Lock, GripHorizontal, Images, Plus, Route,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PanoramaFeature } from "@/api-client";
import { CollectionsModal } from "@/features/social";
import { useAuth } from "@/features/auth";
import { useFeatureFlag } from "@/features/feature-flags";
import { useTheme } from "@/features/theme";

const API = API_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Explore mode registry  (add new modes here — no other changes needed)
// ─────────────────────────────────────────────────────────────────────────────

export type ExploreMode = "explore" | "tours";

interface ExploreModeDefinition {
  id: ExploreMode;
  label: string;
  icon: LucideIcon;
  desc: string;
}

const EXPLORE_MODES: ExploreModeDefinition[] = [
  { id: "explore", label: "Explore", icon: Compass,  desc: "Discover panoramas on the map" },
  { id: "tours",   label: "Tours",   icon: Map,       desc: "Browse virtual tour collections" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Panorama sub-tab registry  (Trending / New)
// ─────────────────────────────────────────────────────────────────────────────

export type ExploreTabId = "trending" | "new";

interface ExploreTabDef {
  id: ExploreTabId;
  label: string;
  icon: LucideIcon;
  sort: (a: PanoramaFeature, b: PanoramaFeature) => number;
}

export const EXPLORE_TABS: ExploreTabDef[] = [
  {
    id: "trending",
    label: "Trending",
    icon: TrendingUp,
    sort: (a, b) =>
      ((b.properties.average_rating || 0) * 2 + (b.properties.view_count || 0)) -
      ((a.properties.average_rating || 0) * 2 + (a.properties.view_count || 0)),
  },
  {
    id: "new",
    label: "New",
    icon: Sparkles,
    sort: (a, b) => {
      const da = (a.properties as any).created_at ? new Date((a.properties as any).created_at).getTime() : 0;
      const db2 = (b.properties as any).created_at ? new Date((b.properties as any).created_at).getTime() : 0;
      return db2 - da;
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Tours sub-tab registry  (Featured / Recent)
// ─────────────────────────────────────────────────────────────────────────────

type TourSortId = "featured" | "recent";

interface TourTabDef { id: TourSortId; label: string; icon: LucideIcon; }
const TOUR_TABS: TourTabDef[] = [
  { id: "featured", label: "Featured", icon: Star },
  { id: "recent",   label: "Recent",   icon: Sparkles },
];

interface PublicTourCard {
  id: string;
  name: string;
  description?: string | null;
  cover_image_url?: string | null;
  creator_name?: string | null;
  panorama_count: number;
  share_token: string;
  password_protected: boolean;
  created_at?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar tab registry
// To add a new tab: extend SidebarTabId, add an entry to SIDEBAR_TABS,
// and render its content in the "Content" section below.
// ─────────────────────────────────────────────────────────────────────────────

type SidebarTabId = "explore";
// Future tabs: | "studio" | "my_map" | ...

interface SidebarTabDef {
  id: SidebarTabId;
  label: string;
  icon: LucideIcon;
}

const SIDEBAR_TABS: SidebarTabDef[] = [
  { id: "explore", label: "Explore", icon: Compass },
  // ← add new tab definitions here; the tab bar appears automatically
];

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar props
// ─────────────────────────────────────────────────────────────────────────────

interface SidebarProps {
  panoramas: PanoramaFeature[];
  isOpen: boolean;
  onSetOpen: (open: boolean) => void;
  exploreTabId: ExploreTabId;
  onExploreTabChange: (id: ExploreTabId) => void;
  // Filter button (panel is rendered beside the sidebar in page.tsx)
  filterOpen: boolean;
  filterDirty: boolean;
  filteredCount: number;
  onFilterClick: () => void;
  mapStyle?: "dark" | "light" | "satellite";
  routeBuilderOpen?: boolean;
  onAddToRoute?: (feature: PanoramaFeature) => void;
  isRouteBuilderOpen?: boolean;
  onToggleRouteBuilder?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar component
// ─────────────────────────────────────────────────────────────────────────────

export default function Sidebar({
  panoramas, isOpen, onSetOpen, exploreTabId, onExploreTabChange,
  filterOpen, filterDirty, filteredCount, onFilterClick, mapStyle = "dark",
  routeBuilderOpen, onAddToRoute, isRouteBuilderOpen, onToggleRouteBuilder,
}: SidebarProps) {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<SidebarTabId>("explore");

  // Explore mode + dropdown
  const [exploreMode, setExploreMode] = useState<ExploreMode>("explore");
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Tours data
  const [tourSortId, setTourSortId] = useState<TourSortId>("featured");
  const [tours, setTours] = useState<PublicTourCard[]>([]);
  const [toursLoading, setToursLoading] = useState(false);

  const [showCollectionsModal, setShowCollectionsModal] = useState(false);

  const { user } = useAuth();
  const flagVirtualTours = useFeatureFlag("virtual_tours");

  // ── Mobile detection ──
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Filter out Tours mode if feature flag is off
  const availableModes = EXPLORE_MODES.filter(
    (m) => m.id !== "tours" || flagVirtualTours
  );

  // ── Click-outside closes dropdown ──
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowModeDropdown(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ── Fetch public tours whenever Tours mode is active or sort changes ──
  useEffect(() => {
    if (activeTab !== "explore" || exploreMode !== "tours") return;
    setToursLoading(true);
    fetch(`${API}/api/tours/public?sort=${tourSortId}&limit=12`)
      .then((r) => r.json())
      .then((data) => setTours(Array.isArray(data) ? data : []))
      .catch(() => setTours([]))
      .finally(() => setToursLoading(false));
  }, [activeTab, exploreMode, tourSortId]);

  // ── Panorama feed ──
  const activeExploreDef = EXPLORE_TABS.find((t) => t.id === exploreTabId) ?? EXPLORE_TABS[0];
  const exploreFeed = [...panoramas].sort(activeExploreDef.sort).slice(0, 8);

  // ── Current mode label for the Explore button ──
  const currentMode = EXPLORE_MODES.find((m) => m.id === exploreMode) ?? EXPLORE_MODES[0];

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          {isMobile && (
            <motion.div
              key="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[44] bg-black/60 backdrop-blur-sm"
              onClick={() => onSetOpen(false)}
            />
          )}
          <motion.aside
            key="sidebar-panel"
            initial={isMobile ? { y: "100%" } : { x: -20, opacity: 0 }}
            animate={isMobile ? { y: 0 } : { x: 0, opacity: 1 }}
            exit={isMobile ? { y: "100%" } : { x: -20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className={
              isMobile
                ? `pointer-events-auto fixed bottom-0 inset-x-0 z-[45] backdrop-blur-xl rounded-t-2xl p-5 flex flex-col max-h-[75vh] overflow-hidden bg-white/75 border-t border-ink/80 text-tone-700 shadow-[0_-8px_32px_rgba(0,0,0,0.07)]`
                : `pointer-events-auto w-80 backdrop-blur-xl border rounded-2xl p-5 flex flex-col max-h-[70vh] overflow-hidden ${isDark ? "bg-black/50 border-white/10 text-white shadow-2xl" : "bg-white/60 border-white/80 text-neutral-600 shadow-[0_8px_32px_rgba(0,0,0,0.07)]"}`
            }
          >
            {isMobile ? (
              <>
                {/* Drag handle */}
                <div className="flex justify-center mb-3 -mt-1">
                  <GripHorizontal className="w-5 h-5 text-white/20" />
                </div>

                {/* 360Photos + Explore mode + filter — all on one row */}
                <motion.div
                  className="flex items-center gap-2 mb-4"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08, type: "spring", stiffness: 380, damping: 28 }}
                >
                  {/* 360Photos close button */}
                  <button
                    onClick={() => onSetOpen(false)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-600 text-white text-[10px] font-black uppercase tracking-[0.12em] shadow-lg shadow-blue-500/20 shrink-0"
                  >
                    <Images className="w-3.5 h-3.5 shrink-0" />
                    360Photos
                    <ChevronDown className="w-3 h-3 opacity-60" />
                  </button>

                  {/* Explore mode selector — inline on mobile */}
                  {activeTab === "explore" && (
                    <div className="relative flex-1 min-w-0" ref={dropdownRef}>
                      <button
                        onClick={() => setShowModeDropdown((s) => !s)}
                        aria-haspopup="listbox"
                        aria-expanded={showModeDropdown}
                        className={`w-full flex justify-center items-center gap-1.5 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.12em] transition-all border bg-ink/5 border-ink/10 text-ink`}
                      >
                        <currentMode.icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{currentMode.label}</span>
                        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform duration-200 ${showModeDropdown ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {showModeDropdown && (
                          <motion.div
                            initial={{ opacity: 0, y: -6, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -6, scale: 0.97 }}
                            transition={{ duration: 0.12 }}
                            className={`absolute top-full left-0 right-0 mt-1.5 border rounded-xl p-1 z-50 ${isDark ? "bg-neutral-900 border-white/10 shadow-2xl" : "bg-white border-black/[0.08] shadow-[0_8px_24px_rgba(0,0,0,0.12)]"}`}
                          >
                            {availableModes.map((mode) => (
                              <button
                                key={mode.id}
                                onClick={() => { setExploreMode(mode.id); setShowModeDropdown(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all text-left ${
                                  exploreMode === mode.id
                                    ? "bg-blue-600/20 text-blue-600"
                                    :"text-tone-300 hover:bg-ink/5 hover:text-ink"
                                }`}
                              >
                                <mode.icon className="w-3.5 h-3.5 shrink-0" />
                                {mode.label}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Filter */}
                  <button
                    data-tour-id="filter-button"
                    onClick={onFilterClick}
                    title="Filters"
                    className={`relative p-2.5 rounded-xl border transition-all shrink-0 ${
                      filterOpen || filterDirty
                        ? "text-indigo-400 bg-indigo-500/10 border-indigo-500/30"
                        :"text-tone-400 border-ink/10 hover:text-ink hover:bg-ink/5"
                    }`}
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    {filterDirty && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-indigo-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                        {filteredCount}
                      </span>
                    )}
                  </button>

                  {/* Route Builder */}
                  {onToggleRouteBuilder && (
                    <button
                      onClick={onToggleRouteBuilder}
                      title="Route Builder"
                      className={`p-2.5 rounded-xl border transition-all shrink-0 ${
                        isRouteBuilderOpen
                          ? "text-indigo-400 bg-indigo-500/10 border-indigo-500/30"
                          :"text-tone-400 border-ink/10 hover:text-ink hover:bg-ink/5"
                      }`}
                    >
                      <Route className="w-4 h-4" />
                    </button>
                  )}
                </motion.div>
              </>
            ) : (
              /* ── Desktop header: filter + close ── */
              <div className="flex items-center justify-between mb-4 px-1">
                <button
                  data-tour-id="filter-button"
                  onClick={onFilterClick}
                  title="Filters"
                  className={`relative flex items-center gap-1.5 p-1.5 rounded-md transition-all ${
                    filterOpen || filterDirty
                      ? "opacity-100 text-indigo-400 bg-indigo-500/10"
                      :"opacity-50 hover:opacity-100 focus:opacity-100 text-tone-400 hover:text-ink hover:bg-ink/5"
                  }`}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  {filterDirty && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-indigo-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                      {filteredCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => onSetOpen(false)}
                  className={`p-1.5 rounded-md transition-colors text-tone-500 hover:text-ink hover:bg-ink/5`}
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* ── Tab bar — rendered only when multiple tabs are registered ── */}
            {SIDEBAR_TABS.length > 1 && (
              <div className="flex bg-ink/5 rounded-lg p-1 mb-4 shrink-0 gap-1">
                {SIDEBAR_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex justify-center items-center gap-2 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                      activeTab === tab.id
                        ? "bg-blue-600 shadow-md text-white"
                        : "text-tone-400 hover:text-ink hover:bg-ink/5"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* ── Explore mode dropdown — desktop only (mobile version is in the header row) ── */}
            {activeTab === "explore" && !isMobile && (
              <div className="flex bg-ink/5 rounded-lg p-1 mb-4 shrink-0">
                <div className="flex-1 relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowModeDropdown((s) => !s)}
                    aria-haspopup="listbox"
                    aria-expanded={showModeDropdown}
                    aria-label={`${currentMode.label} mode — click to switch mode`}
                    className="w-full flex justify-center items-center gap-1.5 py-2 text-xs font-bold uppercase tracking-wider rounded-md bg-blue-600 shadow-md text-white transition-all"
                  >
                    <currentMode.icon className="w-4 h-4" />
                    {currentMode.label}
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-200 ${showModeDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Mode dropdown */}
                  <AnimatePresence>
                    {showModeDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={{ duration: 0.12 }}
                        className={`absolute top-full left-0 right-0 mt-1.5 border rounded-xl p-1 z-50 ${isDark ? "bg-neutral-900 border-white/10 shadow-2xl" : "bg-white border-black/[0.08] shadow-[0_8px_24px_rgba(0,0,0,0.12)]"}`}
                      >
                        {availableModes.map((mode) => (
                          <button
                            key={mode.id}
                            onClick={() => {
                              setExploreMode(mode.id);
                              setShowModeDropdown(false);
                            }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all text-left ${
                              exploreMode === mode.id
                                ? "bg-blue-600/20 text-blue-600"
                                :"text-tone-300 hover:bg-ink/5 hover:text-ink"
                            }`}
                          >
                            <mode.icon className="w-3.5 h-3.5 shrink-0" />
                            <span>{mode.label}</span>
                            <span className="ml-auto text-tone-600 font-normal text-[10px] truncate">{mode.desc}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* ── Content ── */}
            <div className="overflow-y-auto flex-1 pr-1 apple-scrollbar">

              {/* ══════════════════════ EXPLORE TAB ══════════════════════ */}
              {activeTab === "explore" && (
                <AnimatePresence mode="wait">

                  {/* ── Panorama mode ── */}
                  {exploreMode === "explore" && (
                    <motion.div
                      key="pano-mode"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="flex flex-col gap-3"
                    >
                      {/* Category pills */}
                      <div className="flex gap-1.5 flex-wrap px-0.5 py-0.5">
                        {EXPLORE_TABS.map((tab) => {
                          const Icon = tab.icon;
                          const active = tab.id === exploreTabId;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => onExploreTabChange(tab.id)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
                                active
                                  ?"ring-1 ring-tone-400 text-tone-300 bg-ink/5"
                                  : isDark ? "text-neutral-500 hover:text-neutral-300 hover:bg-white/5" : "text-neutral-400 border border-neutral-300 hover:text-neutral-600 hover:bg-black/[0.04]"
                              }`}
                            >
                              <Icon className="w-3 h-3" />
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Panorama feed */}
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={exploreTabId}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.15 }}
                          className="flex flex-col gap-2"
                        >
                          {exploreFeed.length === 0 && (
                            <p className="text-sm text-tone-400 text-center py-6">No locations yet.</p>
                          )}
                          {exploreFeed.map((f) => {
                            const p = f.properties;
                            const dateLabel = (() => {
                              const raw = exploreTabId === "new" ? (p as any).created_at : (p as any).capture_date;
                              return raw
                                ? new Date(raw).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                                : null;
                            })();
                            return (
                              <a
                                key={String(p.id)}
                                href={`#pano=${p.id}&zoom=30`}
                                className="relative flex items-end rounded-xl overflow-hidden border border-ink/10 hover:border-ink/20 transition-colors group min-h-[72px]"
                              >
                                {routeBuilderOpen && onAddToRoute && (
                                  <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddToRoute(f); }}
                                    className="absolute top-2 right-2 z-20 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Add to route"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {(p as any).thumbnail_url ? (
                                  <img
                                    src={(p as any).thumbnail_url}
                                    alt={p.name || ""}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    draggable={false}
                                  />
                                ) : (
                                  <div className="absolute inset-0 bg-blue-500/10 flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-blue-400/50" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-canvas/85 via-canvas/30 to-transparent" />
                                <div className="relative z-10 p-3 w-full">
                                  <h3 className="text-sm font-semibold truncate text-white [text-shadow:0_1px_4px_rgba(0,0,0,1)]">{p.name || "Untitled Panorama"}</h3>
                                  <div className="flex items-center gap-3 text-[11px] mt-1 font-medium [text-shadow:0_1px_3px_rgba(0,0,0,1)]">
                                    <span className="flex items-center gap-1 text-yellow-400"><Star className="w-3 h-3" /> {(p.average_rating || 0).toFixed(1)}</span>
                                    <span className="flex items-center gap-1 text-blue-200"><Eye className="w-3 h-3" /> {p.view_count || 0}</span>
                                    {dateLabel
                                      ? <span className="truncate text-white/60">{dateLabel}</span>
                                      : <span className="truncate text-white/60">By {(p as any).creator_name}</span>}
                                  </div>
                                </div>
                              </a>
                            );
                          })}
                        </motion.div>
                      </AnimatePresence>

                      {/* Manage Collections — bottom of panorama explore */}
                      {user && (
                        <button
                          onClick={() => setShowCollectionsModal(true)}
                          className={`flex items-center justify-between w-full p-3 rounded-xl border transition-colors text-xs font-bold uppercase tracking-wider group mt-1 ${isDark ? "bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/20 text-indigo-200" : "bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700"}`}
                        >
                          <span className="flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Manage Collections</span>
                          <span className={`group-hover:translate-x-1 transition-transform ${isDark ? "text-indigo-400" : "text-indigo-500"}`}>→</span>
                        </button>
                      )}
                    </motion.div>
                  )}

                  {/* ── Tours mode ── */}
                  {exploreMode === "tours" && (
                    <motion.div
                      key="tours-mode"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.15 }}
                      className="flex flex-col gap-3"
                    >
                      {/* Category pills */}
                      <div className="flex gap-1.5 flex-wrap px-0.5 py-0.5">
                        {TOUR_TABS.map((tab) => {
                          const Icon = tab.icon;
                          const active = tab.id === tourSortId;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setTourSortId(tab.id)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
                                active
                                  ?"ring-1 ring-tone-400 text-tone-300 bg-ink/5"
                                  : isDark ? "text-neutral-500 hover:text-neutral-300 hover:bg-white/5" : "text-neutral-400 border border-neutral-300 hover:text-neutral-600 hover:bg-black/[0.04]"
                              }`}
                            >
                              <Icon className="w-3 h-3" />
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Tours feed */}
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={tourSortId}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.15 }}
                          className="flex flex-col gap-2"
                        >
                          {toursLoading ? (
                            <div className="flex justify-center py-8">
                              <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                            </div>
                          ) : tours.length === 0 ? (
                            <div className="flex flex-col items-center gap-2 py-8 text-tone-500">
                              <Map className="w-8 h-8 opacity-20" />
                              <p className="text-xs text-center">No public tours yet. Create a collection and share it!</p>
                            </div>
                          ) : (
                            tours.map((tour) => (
                              <a
                                key={tour.id}
                                href={`/tour/${tour.share_token}`}
                                target="_blank"
                                rel="noreferrer"
                                className={`flex items-center gap-3 p-3 rounded-xl transition-colors border group bg-ink/5 hover:bg-ink/10 border-ink/5`}
                              >
                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-tone-800 shrink-0 flex items-center justify-center border border-ink/5">
                                  {tour.cover_image_url ? (
                                    <img src={tour.cover_image_url} alt={tour.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <Map className="w-4 h-4 text-blue-400/50" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <h3 className={`text-sm font-semibold truncate text-tone-100`}>{tour.name}</h3>
                                    {tour.password_protected && (
                                      <Lock className="w-3 h-3 text-tone-500 shrink-0" />
                                    )}
                                  </div>
                                  <div className={`flex items-center gap-2 text-[11px] mt-1 text-tone-400`}>
                                    <span className="flex items-center gap-1 text-blue-300">
                                      <Map className="w-3 h-3" /> {tour.panorama_count} scene{tour.panorama_count !== 1 ? "s" : ""}
                                    </span>
                                    {tour.creator_name && (
                                      <span className="truncate text-tone-500">by {tour.creator_name}</span>
                                    )}
                                  </div>
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 text-tone-600 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                              </a>
                            ))
                          )}
                        </motion.div>
                      </AnimatePresence>

                      {/* Manage Collections — bottom of tours explore */}
                      {user && (
                        <button
                          onClick={() => setShowCollectionsModal(true)}
                          className={`flex items-center justify-between w-full p-3 rounded-xl border transition-colors text-xs font-bold uppercase tracking-wider group mt-1 ${isDark ? "bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/20 text-indigo-200" : "bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700"}`}
                        >
                          <span className="flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Manage Collections</span>
                          <span className={`group-hover:translate-x-1 transition-transform ${isDark ? "text-indigo-400" : "text-indigo-500"}`}>→</span>
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {/*
               * ── Future tab content ──────────────────────────────────────
               * To add a new tab, append to SIDEBAR_TABS above and render
               * its content here:
               *
               * {activeTab === "my_new_tab" && (
               *   <div>...</div>
               * )}
               */}

            </div>

            {showCollectionsModal && (
              <CollectionsModal onClose={() => setShowCollectionsModal(false)} />
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
